import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const ACCEPTED = 'image/jpeg,image/jpg,image/png';

/**
 * Generic image uploader with drag-and-drop support.
 *
 * Props:
 *   label        — heading text
 *   currentUrl   — currently saved image URL (shown as preview)
 *   onUpload     — async (File) => void  — called when user picks a new file
 *   onDelete     — async ()   => void  — called when user clicks delete (omit to hide button)
 *   size         — preview box size in px (default 200)
 */
const ImageUploader = ({ label, currentUrl, onUpload, onDelete, size = 200 }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // local blob preview

  const handleFile = async (file) => {
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // reset so same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
      setPreviewUrl(null);
    } finally {
      setDeleting(false);
    }
  };

  const displayUrl = previewUrl || currentUrl;

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      {/* Preview / drop zone */}
      <Box
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          width: size,
          height: size,
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          cursor: uploading ? 'default' : 'pointer',
          bgcolor: dragging ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.2s, background-color 0.2s',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="product"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}>
            <UploadIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="caption">
              {t('imageUploader.clickOrDrag')}
              <br />
              {t('imageUploader.formats')}
            </Typography>
          </Box>
        )}

        {/* Loading overlay */}
        {(uploading || deleting) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={32} sx={{ color: 'white' }} />
          </Box>
        )}
      </Box>

      {/* Action buttons below preview */}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {displayUrl ? t('imageUploader.replace') : t('imageUploader.upload')}
        </Button>

        {onDelete && displayUrl && (
          <Tooltip title={t('imageUploader.removeImage')}>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              disabled={deleting}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />
    </Box>
  );
};

export default ImageUploader;
